/**
 * Organization Add/Edit page.
 *
 * Mirrors Angular `organization-addedit` tab model. Business logic lives in
 * `useOrganizationEditPage`; tab UI is split under `components/edit/`.
 */
import { PageShell } from '@/components/layout/PageShell'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { cn } from '@lib/utils/helpers'
import type { OrganizationFormValues } from '../types/schemas'
import { OrganizationEditTabBar } from '../components/edit/OrganizationEditTabBar'
import { OrganizationProfileTab } from '../components/edit/OrganizationProfileTab'
import { OrganizationCertificateTab } from '../components/edit/OrganizationCertificateTab'
import { OrganizationSkipReimbursementTab } from '../components/edit/OrganizationSkipReimbursementTab'
import { OrganizationDefaultFormTab } from '../components/edit/OrganizationDefaultFormTab'
import { OrganizationStockLetterTab } from '../components/edit/OrganizationStockLetterTab'
import { OrganizationHelpAboutTab } from '../components/edit/OrganizationHelpAboutTab'
import { OrganizationInvitePageTab } from '../components/edit/OrganizationInvitePageTab'
import { OrganizationEditDialogs } from '../components/edit/OrganizationEditDialogs'
import { OrganizationEditActionBar } from '../components/edit/OrganizationEditActionBar'
import { useOrganizationEditPage } from '../hooks/useOrganizationEditPage'

export function OrganizationEditPage() {
  const page = useOrganizationEditPage()

  if (page.loading) {
    return (
      <PageShell
        title={page.isEdit ? 'EDIT ORGANIZATION' : 'ADD ORGANIZATION'}
        contentClassName="!w-full !min-w-0 !max-w-none !px-3 !py-4 sm:!px-6 sm:!py-6"
      >
        <ModuleScreenLoading variant="page" data-testid="organization-edit-loading" />
      </PageShell>
    )
  }

  return (
    <PageShell
      title={page.isEdit ? 'EDIT ORGANIZATION' : 'ADD ORGANIZATION'}
      contentClassName="!w-full !min-w-0 !max-w-none !px-3 !py-4 sm:!px-6 sm:!py-6"
    >
      <form
        onSubmit={page.handleSubmit((data) => page.onSubmit(data as OrganizationFormValues))}
        className="w-full min-w-0 max-w-[100%] space-y-0"
      >
        <div
          className={cn(
            'flex min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm',
            'lg:min-h-[calc(100dvh-12.5rem)]',
          )}
        >
          <OrganizationEditTabBar ctx={page} />

          <div
            className={cn(
              'min-h-0 w-full min-w-0 flex-1 overflow-y-auto p-4 sm:p-5',
              '[&_.ck-editor__editable]:min-h-[min(38vh,420px)]',
            )}
          >
            <OrganizationProfileTab ctx={page} />
            <OrganizationCertificateTab ctx={page} />
            <OrganizationSkipReimbursementTab ctx={page} />
            <OrganizationDefaultFormTab ctx={page} />
            <OrganizationStockLetterTab ctx={page} />
            <OrganizationHelpAboutTab ctx={page} />
            <OrganizationInvitePageTab ctx={page} />
          </div>
        </div>

        <OrganizationEditDialogs ctx={page} />
        <OrganizationEditActionBar ctx={page} />
      </form>
    </PageShell>
  )
}
